import { useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { ContextMenu } from '@opallabs/lapidary'
import { useCalendarDragState } from '../dnd'
import { type CalendarItem } from '../types'
import { buildCalendarMenuOptions } from './options'
import { type CalendarMenuProps } from './types'

export type CalendarItemMenuProps = CalendarMenuProps &
  Readonly<{
    /**
     * The bar or chip the menu belongs to. It becomes the trigger itself, so it
     * has to be an element that takes props — a component of ours will not do.
     */
    children: ReactNode
    item: CalendarItem
  }>

/**
 * The right-click menu on one item.
 *
 * The trigger is the bar or chip itself rather than a wrapper, so nothing about
 * the grid's layout changes when a menu is added to it.
 *
 * A menu is not offered while something is being dragged: the same press that
 * starts a drag would otherwise open a menu over the day a person is aiming at.
 */
export const CalendarItemMenu: React.FC<CalendarItemMenuProps> = ({
  capabilities,
  children,
  item,
  onItemAction,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isDragging = useCalendarDragState(
    (state) => state.draggedItemId !== null
  )

  const options = useMemo(
    () => buildCalendarMenuOptions(item, capabilities),
    [capabilities, item]
  )

  if (options.length === 0) return <>{children}</>

  return (
    <ContextMenu.Root onOpenChange={setIsOpen}>
      <ContextMenu.Trigger asChild disabled={isDragging}>
        {children}
      </ContextMenu.Trigger>
      {isOpen && (
        <ContextMenu.Portal>
          <ContextMenu.Content aria-label={item.title}>
            {options.map((option, index) =>
              option.type === 'separator' ? (
                <ContextMenu.Separator key={`separator-${index}`} />
              ) : (
                <ContextMenu.Item
                  key={option.id}
                  destructive={option.isDestructive}
                  onSelect={() => onItemAction({ id: option.id, item })}
                >
                  {option.label}
                </ContextMenu.Item>
              )
            )}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      )}
    </ContextMenu.Root>
  )
}
CalendarItemMenu.displayName = 'Calendar: CalendarItemMenu'

/**
 * Gives a bar or a chip its menu, for the grid's leaves to call.
 *
 * The trigger has to be the element itself: a wrapper would sit between an
 * absolutely positioned bar and the day that places it. `asChild` hands the
 * trigger's own props to that element, which is why the caller passes the
 * `<button>` rather than the component that draws it.
 */
export const withCalendarItemMenu = (
  menu: CalendarMenuProps | undefined,
  item: CalendarItem,
  trigger: ReactElement
): ReactElement =>
  menu === undefined ? (
    trigger
  ) : (
    <CalendarItemMenu {...menu} item={item}>
      {trigger}
    </CalendarItemMenu>
  )
